import React, { useEffect, useRef, useState } from 'react';
import { Card } from '../ui/card';
import { db } from '../../firebase-client';
import { useAuth } from '../../auth/AuthContext';
import { offlineWriteMessage, useOnlineRefreshSignal, useOnlineStatus } from '../../utils/onlineStatus';
import {
  collection,
  getDocs,
  getDocsFromServer,
  getDoc,
  deleteDoc,
  doc,
  query,
  orderBy
} from 'firebase/firestore';
import { reportCaughtError } from '../../utils/clientErrorReporting';
import './notifications.css';
import { listAccessibleBoards } from '../../utils/boardApi';
import { setAppBadgeCount } from '../../utils/appBadge';
import { clearEmailReplyNotification, isEmailReplyNotification } from '../../utils/emailReplyNotifications';

const JOIN_PATH_REGEX = /^\/join\/([^/?#]+)/;
const EVENT_COMMENTS_BOARD_ID = 'events';
const NOTIFICATION_FILTER_CACHE_TTL_MS = 2 * 60 * 1000;
const BOARD_ACCESS_CACHE_PREFIX = 'memberNest.notificationBoardAccess';
const TEAM_COMMENTS_CACHE_PREFIX = 'memberNest.notificationTeamComments';
const boardAccessCache = new Map();
const teamCommentsCache = new Map();

export function clearNotificationFilterCachesForTest() {
  boardAccessCache.clear();
  teamCommentsCache.clear();
}

function nowMs() {
  return Date.now();
}

function cacheKey(prefix, id) {
  return `${prefix}:${encodeURIComponent(String(id || ''))}`;
}

function readSessionCache(prefix, id) {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(cacheKey(prefix, id));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.expiresAt || parsed.expiresAt <= nowMs()) {
      window.sessionStorage.removeItem(cacheKey(prefix, id));
      return null;
    }
    return parsed.value;
  } catch (err) {
    return null;
  }
}

function writeSessionCache(prefix, id, value) {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(cacheKey(prefix, id), JSON.stringify({
      expiresAt: nowMs() + NOTIFICATION_FILTER_CACHE_TTL_MS,
      value,
    }));
  } catch (err) {}
}

function readMemoryCache(cache, id) {
  const entry = cache.get(id);
  if (!entry) return null;
  if (entry.expiresAt <= nowMs()) {
    cache.delete(id);
    return null;
  }
  return entry.value;
}

function writeMemoryCache(cache, id, value) {
  cache.set(id, {
    expiresAt: nowMs() + NOTIFICATION_FILTER_CACHE_TTL_MS,
    value,
  });
}

async function getAccessibleBoardIds(teamId) {
  const memoryValue = readMemoryCache(boardAccessCache, teamId);
  if (Array.isArray(memoryValue)) return new Set(memoryValue);

  const sessionValue = readSessionCache(BOARD_ACCESS_CACHE_PREFIX, teamId);
  if (Array.isArray(sessionValue)) {
    writeMemoryCache(boardAccessCache, teamId, sessionValue);
    return new Set(sessionValue);
  }

  const result = await listAccessibleBoards({ teamId });
  const boardIds = (Array.isArray(result?.boards) ? result.boards : [])
    .map((board) => board?.id)
    .filter(Boolean);
  writeMemoryCache(boardAccessCache, teamId, boardIds);
  writeSessionCache(BOARD_ACCESS_CACHE_PREFIX, teamId, boardIds);
  return new Set(boardIds);
}

async function getTeamCommentsEnabled(teamId) {
  const memoryValue = readMemoryCache(teamCommentsCache, teamId);
  if (typeof memoryValue === 'boolean') return memoryValue;

  const sessionValue = readSessionCache(TEAM_COMMENTS_CACHE_PREFIX, teamId);
  if (typeof sessionValue === 'boolean') {
    writeMemoryCache(teamCommentsCache, teamId, sessionValue);
    return sessionValue;
  }

  const teamSnap = await getDoc(doc(db, 'teams', teamId));
  const commentsEnabled = teamSnap.exists() ? teamSnap.data()?.eventSettings?.commentsEnabled === true : false;
  writeMemoryCache(teamCommentsCache, teamId, commentsEnabled);
  writeSessionCache(TEAM_COMMENTS_CACHE_PREFIX, teamId, commentsEnabled);
  return commentsEnabled;
}

function getActionSearchParams(actionUrl = '') {
  const queryStart = actionUrl.indexOf('?');
  if (queryStart === -1) return new URLSearchParams();
  return new URLSearchParams(actionUrl.slice(queryStart + 1));
}

function getBoardNotificationRef(note = {}) {
  const actionUrl = typeof note?.actionUrl === 'string' ? note.actionUrl.trim() : '';
  const params = getActionSearchParams(actionUrl);
  const teamId = note.teamId || params.get('teamId') || '';
  const boardId = note.boardId || params.get('boardId') || '';
  const kind = String(note.kind || '');
  const looksLikeBoardNotification = kind.startsWith('board') || actionUrl.startsWith('/messages?');

  if (!looksLikeBoardNotification && !teamId && !boardId) return null;
  if (!teamId || !boardId) return null;
  return { teamId, boardId, actionUrl };
}

function isEventCommentsNotification(note = {}, ref = {}) {
  return ref.boardId === EVENT_COMMENTS_BOARD_ID
    || note.systemType === 'event_comments'
    || ref.actionUrl?.includes('/calendar?');
}

function notificationActionUrl(note = {}) {
  const actionUrl = typeof note?.actionUrl === 'string' ? note.actionUrl.trim() : '';
  if (actionUrl) return actionUrl;
  return typeof note?.link === 'string' ? note.link.trim() : '';
}

export function notificationDisplayCopy(note = {}) {
  const title = typeof note?.title === 'string' ? note.title.trim() : '';
  const message = typeof note?.message === 'string' ? note.message.trim() : '';
  const body = typeof note?.body === 'string' ? note.body.trim() : '';
  if (message) return { title: title && title !== message ? title : '', message };
  if (body) return { title, message: body };
  if (title) return { title: '', message: title };
  return { title: '', message: 'Notification details unavailable.' };
}

export default function Notifications() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadedNotificationsUserId, setLoadedNotificationsUserId] = useState('');
  const [hasOverflow, setHasOverflow] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const scrollRef = useRef(null);
  const online = useOnlineStatus();
  const onlineRefreshSignal = useOnlineRefreshSignal();

  useEffect(() => {
    const fetchNotifications = async () => {
      if (!user?.uid) {
        setNotifications([]);
        setLoadedNotificationsUserId('');
        setAppBadgeCount(0);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const q = query(
          collection(db, 'notifications', user.uid, 'items'),
          orderBy('timestamp', 'desc')
        );
        const [snap, userSnap] = await Promise.all([
          (typeof getDocsFromServer === 'function' ? getDocsFromServer(q) : getDocs(q)),
          getDoc(doc(db, 'users', user.uid)),
        ]);
        const teamRoles = userSnap.exists() ? (userSnap.data()?.team_roles || {}) : {};

        const items = snap.docs.map((noteDoc) => ({
          id: noteDoc.id,
          ...noteDoc.data()
        }));

        const staleNotes = items.filter((note) => {
          const actionUrl = typeof note?.actionUrl === 'string' ? note.actionUrl.trim() : '';
          const match = actionUrl.match(JOIN_PATH_REGEX);
          if (!match?.[1]) return false;
          const teamId = decodeURIComponent(match[1]);
          return Boolean(teamId && teamRoles?.[teamId]);
        });

        const boardRefs = items
          .map((note) => ({ note, ref: getBoardNotificationRef(note) }))
          .filter((entry) => entry.ref);

        const teamIds = Array.from(new Set(boardRefs.map((entry) => entry.ref.teamId)));
        const eventCommentTeamIds = Array.from(new Set(boardRefs
          .filter(({ note, ref }) => isEventCommentsNotification(note, ref))
          .map((entry) => entry.ref.teamId)));
        const [accessibleEntries, teamCommentEntries] = await Promise.all([
          Promise.all(teamIds.map(async (teamId) => {
            try {
              return [teamId, await getAccessibleBoardIds(teamId)];
            } catch (err) {
              return [teamId, null];
            }
          })),
          Promise.all(eventCommentTeamIds.map(async (teamId) => {
            try {
              return [teamId, await getTeamCommentsEnabled(teamId)];
            } catch (err) {
              return [teamId, null];
            }
          })),
        ]);

        const accessibleBoardsByTeam = new Map(accessibleEntries);
        const commentsEnabledByTeam = new Map(teamCommentEntries);
        const staleBoardNotes = boardRefs
          .filter(({ note, ref }) => {
            const accessibleBoardIds = accessibleBoardsByTeam.get(ref.teamId);
            if (!accessibleBoardIds) return false;

            if (!accessibleBoardIds.has(ref.boardId)) return true;

            if (isEventCommentsNotification(note, ref)) {
              const commentsEnabled = commentsEnabledByTeam.get(ref.teamId);
              if (commentsEnabled === false) return true;
            }

            return false;
          })
          .map(({ note }) => note);

        const notesToDelete = [...staleNotes, ...staleBoardNotes];
        if (notesToDelete.length > 0) {
          await Promise.all(
            notesToDelete.map((note) =>
              deleteDoc(doc(db, 'notifications', user.uid, 'items', note.id)).catch(() => {})
            )
          );
        }

        const staleIds = new Set(notesToDelete.map((note) => note.id));
        const visibleNotifications = items.filter((note) => !staleIds.has(note.id));
        setNotifications(visibleNotifications);
        setAppBadgeCount(visibleNotifications.length);
      } catch (err) {
        reportCaughtError(err, { source: 'notifications.load', severity: 'error' });
      } finally {
        setLoadedNotificationsUserId(user.uid);
        setLoading(false);
      }
    };

    fetchNotifications();
  }, [onlineRefreshSignal, user?.uid]);

  const handleDelete = async (id) => {
    if (!user?.uid) return;
    if (!online) return;
    try {
      await deleteDoc(doc(db, 'notifications', user.uid, 'items', id));
      setNotifications((prev) => {
        const next = prev.filter(n => n.id !== id);
        setAppBadgeCount(next.length);
        return next;
      });
    } catch (err) {}
  };

  const handleDeleteAll = async () => {
    if (!user?.uid) return;
    if (!online) return;
    if (!window.confirm(`Delete all ${notifications.length} notifications? This cannot be undone.`)) return;
    try {
      const q = query(collection(db, 'notifications', user.uid, 'items'));
      const snap = await getDocs(q);
      const batchDeletes = snap.docs.map(docSnap =>
        deleteDoc(doc(db, 'notifications', user.uid, 'items', docSnap.id)).catch(() => {})
      );
      await Promise.all(batchDeletes);
      setNotifications([]);
      setAppBadgeCount(0);
    } catch (err) {}
  };

  const handleNotificationClick = (note) => {
    if (!user?.uid || !isEmailReplyNotification(note)) return;
    clearEmailReplyNotification({
      db,
      userId: user.uid,
      notificationId: note.id,
    }).catch(() => {});
    setNotifications((prev) => {
      const next = prev.filter((entry) => entry.id !== note.id);
      setAppBadgeCount(next.length);
      return next;
    });
  };

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const updateScrollState = () => {
      const overflow = container.scrollHeight > container.clientHeight;
      const atBottom =
        container.scrollTop + container.clientHeight >= container.scrollHeight - 1;
      setHasOverflow(overflow);
      setIsAtBottom(atBottom || !overflow);
    };

    updateScrollState();
    container.addEventListener('scroll', updateScrollState);
    return () => container.removeEventListener('scroll', updateScrollState);
  }, [notifications]);

  if (!user?.uid) return null;

  const shouldCapHeight = notifications.length > 3;
  const hasNotifications = notifications.length > 0;
  const hasResolvedNotifications = loadedNotificationsUserId === user.uid;

  return (
    <Card title="Recent Notifications" className="dashboard-card">
      {loading && !hasResolvedNotifications ? (
        <p>Loading...</p>
      ) : !hasNotifications ? (
        <p style={{ color: 'var(--clr-text-muted)' }}>
          You have no notifications.
        </p>
      ) : (
        <>
          {notifications.length > 0 && (
            <button
              className="delete-all-btn"
              onClick={handleDeleteAll}
              disabled={!online}
              style={{ marginBottom: '1rem' }}
            >
              Delete All
            </button>
          )}
          {!online && (
            <p className="text-muted" style={{ marginTop: 0 }}>
              {offlineWriteMessage('delete notifications')}
            </p>
          )}

          <div className="notifications-scroll-wrapper">
            <div
              ref={scrollRef}
              className="notifications-scroll"
              style={
                shouldCapHeight
                  ? { maxHeight: '260px', overflowY: 'auto', paddingRight: '4px' }
                  : {}
              }
            >
              <ul className="notifications-list">
                {notifications.map(note => {
                  const copy = notificationDisplayCopy(note);
                  const notificationText = (
                    <>
                      {copy.title && <strong className="notification-title">{copy.title}</strong>}
                      <span className="notification-message">{copy.message}</span>
                    </>
                  );
                  return (
                    <li key={note.id} className="notification-item">
                      <div className="notification-content">
                        {notificationActionUrl(note) ? (
                          <p>
                            <a
                              href={notificationActionUrl(note)}
                              className="notification-link"
                              onClick={() => handleNotificationClick(note)}
                            >
                              {notificationText}
                            </a>
                          </p>
                        ) : (
                          <p className="notification-copy">{notificationText}</p>
                        )}
                        <span className="notification-meta">
                          {note.timestamp?.toDate?.().toLocaleString() || ''}
                        </span>
                      </div>
                      <button
                        className="delete-btn"
                        onClick={() => handleDelete(note.id)}
                        disabled={!online}
                        title="Delete notification"
                      >
                        ×
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>

            {hasOverflow && !isAtBottom && (
              <div className="notifications-scroll-overlay" aria-hidden="true">
                <span className="notifications-scroll-arrow" />
              </div>
            )}
          </div>
        </>
      )}
    </Card>
  );
}
